import * as geom from "js/core/utilities/geom";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { Chart } from "js/elements/elements/Chart";
import { PieChart } from "js/elements/elements/PieChart";
import { TextGroup } from "js/elements/base/TextGroup";
import { getDefaultChartModel } from "common/slideTemplates/templatesData/templates";

class Dashboard extends CollectionElement {
    getCanvasMargins() {
        // We don't want charts to be overlapped by the branding, so we limit
        // margin bottom to 60px
        const margins = super.getCanvasMargins();
        margins.bottom = Math.max(margins.bottom, 60);
        return margins;
    }

    get maxItemCount() {
        return 4;
    }

    getChildItemType() {
        return DashboardItem;
    }

    get defaultItemData() {
        return {
            elementType: "chart",
            chart: getDefaultChartModel("line")
        };
    }

    get showTitle() {
        return this.model.showTitle || false;
    }

    get preventTransitionDuringRender() {
        return true;
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        var itemBounds = [];
        switch (this.itemElements.length) {
            case 1:
                itemBounds.push(new geom.Rect(0, 0, size));
                break;
            case 2:
                switch (this.model.layout) {
                    case "2-charts-vertical":
                        itemBounds.push(new geom.Rect(0, 0, size.width, size.height / 2 - styles.vGap / 2));
                        itemBounds.push(new geom.Rect(0, size.height / 2 + styles.vGap / 2, size.width, size.height / 2 - styles.vGap / 2));
                        break;
                    case "2-charts-horizontal":
                    default:
                        itemBounds.push(new geom.Rect(0, 0, size.width / 2 - styles.hGap / 2, size.height));
                        itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, 0, size.width / 2 - styles.hGap / 2, size.height));
                        break;
                }
                break;
            case 3:
                switch (this.model.layout) {
                    case "3-charts-horizontal":
                        let thirdWidth = (size.width - styles.hGap * 2) / 3;
                        itemBounds.push(new geom.Rect(0, 0, thirdWidth, size.height));
                        itemBounds.push(new geom.Rect(thirdWidth + styles.hGap, 0, thirdWidth, size.height));
                        itemBounds.push(new geom.Rect(size.width - thirdWidth, 0, thirdWidth, size.height));
                        break;
                    case "3-charts-right":
                        itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, 0, size.width / 2 - styles.hGap / 2, size.height));
                        itemBounds.push(new geom.Rect(0, 0, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        itemBounds.push(new geom.Rect(0, 0 + size.height / 2 + styles.vGap / 2, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        break;
                    case "3-charts-top":
                        itemBounds.push(new geom.Rect(0, 0, size.width, size.height / 2 - styles.vGap / 2));
                        itemBounds.push(new geom.Rect(0, size.height / 2 + styles.vGap / 2, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, size.height / 2 + styles.vGap / 2, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        break;
                    case "3-charts-bottom":
                        itemBounds.push(new geom.Rect(0, 0, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, 0, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        itemBounds.push(new geom.Rect(0, size.height / 2 + styles.vGap / 2, size.width, size.height / 2 - styles.vGap / 2));
                        break;
                    case "3-charts-left":
                    default:
                        itemBounds.push(new geom.Rect(0, 0, size.width / 2 - styles.hGap / 2, size.height));
                        itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, 0, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, 0 + size.height / 2 + styles.vGap / 2, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                        break;
                }
                break;
            case 4:
            default:
                itemBounds.push(new geom.Rect(0, 0, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                itemBounds.push(new geom.Rect(0, size.height / 2 + styles.vGap / 2, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, 0, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                itemBounds.push(new geom.Rect(size.width / 2 + styles.hGap / 2, 0 + size.height / 2 + styles.vGap / 2, size.width / 2 - styles.hGap / 2, size.height / 2 - styles.vGap / 2));
                break;
        }

        let maxTitleHeight = 0;
        if (this.showTitle) {
            for (let i = 0; i < this.itemElements.length; i++) {
                let titleSize = this.itemElements[i].calcTitleSize(styles.DashboardItem.chartTitle, itemBounds[i].size);
                maxTitleHeight = Math.max(titleSize.height, maxTitleHeight);
            }
        }

        for (let i = 0; i < this.itemElements.length; i++) {
            let itemProps = children.setProps(this.itemElements[i], itemBounds[i].size, { titleHeight: maxTitleHeight });
            itemProps.bounds = itemBounds[i];
        }
        return { size };
    }

    registerAnimations(animationManager) {
        for (let item of this.itemElements) {
            item.registerAnimations(animationManager);
        }
    }

    setItemCount(count) {
        if (count > this.itemElements.length) {
            for (let i = this.itemElements.length; i < count; i++) {
                this.addItem(this.defaultItemData);
            }
        } else if (count < this.itemElements.length) {
            for (let i = this.itemElements.length; i > count; i--) {
                this.deleteItem(this.itemElements[i - 1].id);
            }
        }
    }

    _migrate_8() {
        // migrate layout because default value has changed
        if (this.model.items.length == 2 && this.model.layout == null) {
            this.model.layout = "2-charts-vertical";
        }
    }
}

class DashboardItem extends CollectionItemElement {
    get showTitle() {
        return this.parentElement.showTitle;
    }

    get selectionPadding() {
        return 10;
    }

    get canRollover() {
        return true;
    }

    get selectionUIType() {
        switch (this.infographic.type) {
            case "piechart":
                break;
            default:
                return "ChartSelection";
        }
    }

    get rolloverPadding() {
        return { left: 0, right: 0, top: 0, bottom: 15 };  // allows extra space for category axis title clicking
    }

    _build() {
        if (this.showTitle) {
            if (!this.model.chartTitle) {
                this.model.chartTitle = {};
            }
            this.chartTitle = this.addElement("chartTitle", () => TextGroup, {
                model: this.model.chartTitle,
                showAddButton: false,
                title: {
                    placeholder: "Type title"
                }

            });
        }

        switch (this.model.elementType) {
            case "chart": {
                let firstSeriesType = this.model.chart.chartData.series[0].type;
                this.infographic = this.addElement("infographic", () => Chart, {
                    model: this.model.chart,
                    rollover: firstSeriesType === "waterfall" ? "WaterfallChartRollover" : "ChartRollover"
                });
                break;
            }
            case "piechart":
                this.infographic = this.addElement("infographic", () => PieChart, {
                    model: this.model.piechart,
                });
                break;
        }
    }

    calcTitleSize(styles, size) {
        if (this.showTitle) {
            switch (this.model.elementType) {
                case "chart":
                    styles.marginBottom = 15;
                    break;
                case "piechart":
                    styles.marginBottom = 20;
                    break;
            }
            return this.chartTitle.calcProps(size, { styles }).size;
        } else {
            return new geom.Size(0, 0);
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let y = options.titleHeight;
        if (this.showTitle) {
            let titleProps = children.setProps(this.chartTitle, new geom.Size(size.width, options.titleHeight));
            titleProps.bounds = new geom.Rect(0, 0, titleProps.size);
            this.titleHeight = y;
        } else {
            this.titleHeight = 0;
        }

        let availableHeight = size.height - y;

        let infographicProps = children.setProps(this.infographic, new geom.Size(size.width, availableHeight));
        infographicProps.bounds = new geom.Rect(0, y, infographicProps.size);

        return { size };
    }
}

export { Dashboard, DashboardItem };

export const elements = {
    Dashboard
};
